import { useParams } from '@tanstack/react-router'
import { useRolodexEntries } from 'features/rolodex'
import { useMemo } from 'react'
import { RolodexEntryType } from 'types'

export interface ChatbotContextInfo {
  organizationIds: string[]
  siteId: string | undefined
  url: string
  pageTitle: string
  urlParams: Record<string, unknown>
  urlSearchParams: Record<string, unknown>
}

function getStringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

export const useChatbotContext = (
  urlSearchParams: Record<string, unknown>
): ChatbotContextInfo => {
  const params = useParams({ strict: false })
  const siteId =
    getStringValue(params.siteID) ??
    getStringValue(urlSearchParams.siteId) ??
    getStringValue(urlSearchParams.siteID)
  const explicitOrgId =
    getStringValue(params.orgID) ??
    getStringValue(urlSearchParams.orgId) ??
    getStringValue(urlSearchParams.orgID)

  const { entries: sites } = useRolodexEntries(
    siteId && !explicitOrgId
      ? {
          ids: [siteId],
          type: RolodexEntryType.SITE,
        }
      : undefined
  )

  const organizationIds = useMemo(() => {
    if (explicitOrgId) return [explicitOrgId]
    if (!sites || sites.length === 0) return []
    const site = sites[0]
    const orgId = site?.tags?.organization
    return orgId ? [orgId] : []
  }, [explicitOrgId, sites])

  return useMemo(
    () => ({
      organizationIds,
      siteId,
      url: window.location.href,
      pageTitle: document.title,
      urlParams: params,
      urlSearchParams,
    }),
    [organizationIds, siteId, params, urlSearchParams]
  )
}
